import { useState, useRef, useEffect } from "react";
import { formatDateOnly, parseDateOnly } from "@/lib/date-only";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOfflineQuery } from "@/hooks/use-offline-data";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { useFileUpload } from "@/hooks/use-file-upload";
import { DocumentCapture, canCaptureCamera } from "@/components/documents/document-capture";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { MessageText } from "@/components/ui/message-text";
import {
  Award,
  Plus,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Camera,
  Upload,
  FileText,
  X,
  Loader2,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Clock,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { CertWalletCard } from "@/components/certifications/cert-wallet-card";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/my-certifications")({
  validateSearch: (s: Record<string, unknown>): { upload?: string } =>
    typeof s.upload === "string" ? { upload: s.upload } : {},
  component: MyCertificationsPage,
});

interface CertType {
  id: string;
  name: string;
  description: string | null;
  category: "trade" | "safety" | "training";
  requiresExpiry: boolean;
  requiresFile: boolean;
  defaultValidityMonths: number | null;
}

interface UserCert {
  id: string;
  certTypeId: string | null;
  certTypeName: string | null;
  certTypeCategory: "trade" | "safety" | "training" | null;
  customCertName: string | null;
  customCategory: "trade" | "safety" | "training" | null;
  certNumber: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
  status: string;
  reviewStatus: string;
  fileId: string | null;
  fileId2: string | null;
  notes: string | null;
  /** 1 when this expired record has been replaced by a current one of the same credential (archived). */
  superseded?: number | boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  trade: "Trade Tickets & Licences",
  safety: "Safety Certifications",
  training: "Training Records",
};

const CATEGORY_COLORS: Record<string, { dot: string; text: string; bg: string }> = {
  trade: { dot: "bg-blue-500", text: "text-blue-700 dark:text-blue-300", bg: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700" },
  safety: { dot: "bg-orange-500", text: "text-orange-700 dark:text-orange-300", bg: "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700" },
  training: { dot: "bg-purple-500", text: "text-purple-700 dark:text-purple-300", bg: "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700" },
};

function computeLiveStatus(cert: UserCert): string {
  if (cert.status === "revoked") return "revoked";
  if (!cert.expiryDate) return cert.status || "valid";
  const expiry = parseDateOnly(cert.expiryDate);
  const now = new Date();
  const thirtyDays = new Date();
  thirtyDays.setDate(now.getDate() + 30);
  if (expiry < now) return "expired";
  if (expiry < thirtyDays) return "expiring_soon";
  return "valid";
}

export interface MyCertAssignment {
  completionId: string;
  assignmentId: string;
  status: "assigned" | "submitted" | "approved";
  claimNote: string | null;
  submittedAt: string | null;
  certTypeId: string;
  certTypeName: string | null;
  message: string | null;
  dueDate: string | null;
}

function MyCertificationsPage() {
  const queryClient = useQueryClient();
  // ?upload=<certTypeId> opens the add form for that credential (from My Assignments).
  const { upload: uploadParam } = Route.useSearch();
  const [showForm, setShowForm] = useState(!!uploadParam);
  const [uploadCertTypeId, setUploadCertTypeId] = useState<string | null>(uploadParam ?? null);
  const [editingCert, setEditingCert] = useState<UserCert | null>(null);
  const [deletingCert, setDeletingCert] = useState<UserCert | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const { data, isLoading } = useOfflineQuery({
    queryKey: ["my-certifications"],
    queryFn: () => apiGet<{ data: UserCert[] }>("/api/certifications/mine"),
    cacheKey: "offline:my-certs",
  });

  const { data: assignmentsData } = useQuery({
    queryKey: ["my-cert-assignments"],
    queryFn: () => apiGet<{ data: MyCertAssignment[] }>("/api/certifications/my-assignments"),
    staleTime: 30_000,
  });
  const assignments = (assignmentsData?.data ?? []).filter((a) => a.status !== "approved");

  const certs = data?.data ?? [];
  // The archive holds revoked records and expired ones that a current record has replaced.
  // Valid credentials (even with a newer one on file), credentials with no expiry date, and expired
  // ones with no replacement stay in the main list; an expired one keeps warning until renewed.
  const isArchived = (c: UserCert) => {
    const s = computeLiveStatus(c);
    return s === "revoked" || (s === "expired" && !!c.superseded);
  };
  const activeCerts = certs.filter((c) => !isArchived(c));
  const historyCerts = certs.filter(isArchived);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["my-certifications"] });
    queryClient.invalidateQueries({ queryKey: ["my-cert-assignments"] });
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-24">
      <div className="flex items-center justify-between pt-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">My Credentials</h1>
          <Link to="/my-assignments" search={{ tab: "certs" }} className="text-xs text-paris-red-text underline underline-offset-2">My assignments</Link>
        </div>
        <Button
          size="sm"
          onClick={() => { setShowForm(!showForm); setUploadCertTypeId(null); }}
          variant={showForm ? "outline" : "default"}
        >
          {showForm ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {showForm ? "Cancel" : "Add"}
        </Button>
      </div>

      {assignments.length > 0 && (
        <AssignedCertsBanner
          assignments={assignments}
          onUpload={(certTypeId) => { setUploadCertTypeId(certTypeId); setShowForm(true); }}
          onClaimed={invalidate}
        />
      )}

      {showForm && (
        <AddCertForm
          initialCertTypeId={uploadCertTypeId ?? undefined}
          onSuccess={() => { setShowForm(false); setUploadCertTypeId(null); invalidate(); }}
          onCancel={() => { setShowForm(false); setUploadCertTypeId(null); }}
        />
      )}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-2xl" />
          ))}
        </div>
      )}

      {!isLoading && certs.length === 0 && !showForm && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Award className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">No credentials yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Tap "Add" to upload your certifications and trade tickets.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && activeCerts.length > 0 && (
        <div className="space-y-3">
          {activeCerts.map((cert) => (
            <CertCardWithActions
              key={cert.id}
              cert={cert}
              onEdit={() => setEditingCert(cert)}
              onDelete={() => setDeletingCert(cert)}
            />
          ))}
        </div>
      )}

      {!isLoading && historyCerts.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text-primary py-2"
          >
            {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showHistory ? "Hide" : "Show"} archive ({historyCerts.length} archived)
          </button>
          {showHistory && (
            <div className="space-y-3 mt-2 opacity-70">
              {historyCerts.map((cert) => (
                <CertCardWithActions
                  key={cert.id}
                  cert={cert}
                  onEdit={() => setEditingCert(cert)}
                  onDelete={() => setDeletingCert(cert)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {editingCert && (
        <EditCertSheet
          cert={editingCert}
          onSuccess={() => { setEditingCert(null); invalidate(); }}
          onClose={() => setEditingCert(null)}
        />
      )}

      {deletingCert && (
        <DeleteCertModal
          cert={deletingCert}
          onSuccess={() => { setDeletingCert(null); invalidate(); }}
          onClose={() => setDeletingCert(null)}
        />
      )}
    </div>
  );
}

// --- Assigned certifications warning banner ---------------------------------

export function AssignedCertsBanner({
  assignments,
  onUpload,
  onClaimed,
}: {
  assignments: MyCertAssignment[];
  onUpload: (certTypeId: string) => void;
  onClaimed: () => void;
}) {
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [claimNote, setClaimNote] = useState("");

  const claimMutation = useMutation({
    mutationFn: ({ completionId, note }: { completionId: string; note: string }) =>
      apiPost(`/api/certifications/assignments/${completionId}/claim-existing`, { note: note || undefined }),
    onSuccess: () => {
      toast.success("Sent to your admin for approval");
      setClaimingId(null);
      setClaimNote("");
      onClaimed();
    },
    onError: (err: Error) => toast.error(err.message || "Failed to submit"),
  });

  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          {assignments.length} certification{assignments.length !== 1 ? "s" : ""} required
        </p>
      </div>

      <div className="space-y-2">
        {assignments.map((a) => (
          <div key={a.completionId} className="rounded-lg bg-amber-500/10 px-3 py-2.5 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  {a.certTypeName ?? "Certification"}
                </p>
                {a.dueDate && (
                  <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80">
                    Due {formatDateOnly(a.dueDate)}
                  </p>
                )}
                {a.message && (
                  <MessageText text={a.message} className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-0.5" />
                )}
              </div>
              {a.status === "submitted" ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-400 shrink-0">
                  <Clock className="h-3 w-3" /> Awaiting Approval
                </span>
              ) : null}
            </div>

            {a.status === "assigned" && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={() => onUpload(a.certTypeId)}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Upload
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => { setClaimingId(claimingId === a.completionId ? null : a.completionId); setClaimNote(""); }}
                >
                  I Already Have This
                </Button>
              </div>
            )}

            {claimingId === a.completionId && (
              <div className="space-y-2 pt-1">
                <Input
                  value={claimNote}
                  onChange={(e) => setClaimNote(e.target.value)}
                  placeholder={`Optional — e.g. "filed under a different name"`}
                  className="h-8 text-xs"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-7 text-xs flex-1"
                    disabled={claimMutation.isPending}
                    onClick={() => claimMutation.mutate({ completionId: a.completionId, note: claimNote.trim() })}
                  >
                    {claimMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send to Admin"}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setClaimingId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {a.status === "submitted" && a.claimNote && (
              <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 italic">
                "I already have this — {a.claimNote}"
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Cert card with edit/delete actions ------------------------------------

function CertCardWithActions({
  cert,
  onEdit,
  onDelete,
}: {
  cert: UserCert;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="relative group">
      <CertWalletCard
        cert={{
          id: cert.id,
          certTypeName: cert.certTypeName,
          customCertName: cert.customCertName,
          certNumber: cert.certNumber,
          issuedDate: cert.issuedDate,
          expiryDate: cert.expiryDate,
          status: cert.status,
          reviewStatus: cert.reviewStatus,
          fileId: cert.fileId,
          fileId2: cert.fileId2,
          notes: cert.notes,
          category: cert.certTypeCategory ?? cert.customCategory ?? null,
        }}
      />
      {/* Action buttons — always visible on mobile, hover on desktop */}
      <div className="absolute top-2 right-2 flex gap-1.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          aria-label="Edit"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600/80 text-white hover:bg-red-700"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// --- Edit cert sheet -------------------------------------------------------

function EditCertSheet({
  cert,
  onSuccess,
  onClose,
}: {
  cert: UserCert;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const { upload, isUploading, isCompressing, progress } = useFileUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Second page (e.g. back of a card/license) -- optional, own upload slot.
  const { upload: upload2, isUploading: isUploading2, isCompressing: isCompressing2, progress: progress2 } = useFileUpload();
  const fileInputRef2 = useRef<HTMLInputElement>(null);
  const cameraInputRef2 = useRef<HTMLInputElement>(null);
  const [cameraTarget, setCameraTarget] = useState<1 | 2 | null>(null);
  const [fileId2, setFileId2] = useState<string | null>(cert.fileId2 ?? null);
  const [fileName2, setFileName2] = useState<string | null>(cert.fileId2 ? "Current back page" : null);
  const [showBackPage, setShowBackPage] = useState(!!cert.fileId2);

  const [certNumber, setCertNumber] = useState(cert.certNumber ?? "");
  const [issuedDate, setIssuedDate] = useState(cert.issuedDate ?? "");
  const [expiryDate, setExpiryDate] = useState(cert.expiryDate ?? "");
  const [notes, setNotes] = useState(cert.notes ?? "");
  const [fileId, setFileId] = useState<string | null>(cert.fileId ?? null);
  const [fileName, setFileName] = useState<string | null>(cert.fileId ? "Current document" : null);

  // Password + reason confirmation
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [password, setPassword] = useState("");
  const [editReason, setEditReason] = useState("");
  const [verifying, setVerifying] = useState(false);

  const certName = cert.certTypeName ?? cert.customCertName ?? "Certification";

  const editMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPatch(`/api/certifications/${cert.id}`, data),
    onSuccess: () => {
      toast.success("Certification updated");
      onSuccess();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update");
    },
  });

  async function uploadFirstPage(file: File) {
    try {
      const result = await upload(file, { fileName: file.name, mimeType: file.type, purpose: "certification" });
      setFileId(result.fileId);
      setFileName(file.name);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFirstPage(file);
    e.target.value = "";
  }

  async function uploadSecondPage(file: File) {
    try {
      const result = await upload2(file, { fileName: file.name, mimeType: file.type, purpose: "certification" });
      setFileId2(result.fileId);
      setFileName2(file.name);
      toast.success("Back page uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function handleFileChange2(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadSecondPage(file);
    e.target.value = "";
  }

  async function handleConfirmAndSave() {
    if (!editReason.trim()) {
      toast.error("Please provide a reason for this change");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setVerifying(true);
    try {
      const res = await apiPost<{ valid: boolean }>("/api/me/verify-password", { currentPassword: password });
      if (!res.valid) {
        toast.error("Incorrect password");
        setVerifying(false);
        return;
      }
    } catch {
      toast.error("Could not verify password");
      setVerifying(false);
      return;
    }
    setVerifying(false);

    const payload: Record<string, unknown> = { editReason: editReason.trim() };
    if (certNumber !== (cert.certNumber ?? "")) payload.certNumber = certNumber || null;
    if (issuedDate !== (cert.issuedDate ?? "")) payload.issuedDate = issuedDate || null;
    if (expiryDate !== (cert.expiryDate ?? "")) payload.expiryDate = expiryDate || null;
    if (notes !== (cert.notes ?? "")) payload.notes = notes || null;
    if (fileId !== cert.fileId) payload.fileId = fileId;
    if (fileId2 !== (cert.fileId2 ?? null)) payload.fileId2 = fileId2;

    editMutation.mutate(payload);
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit: {certName}</SheetTitle>
          <SheetDescription>Changes require your password and a reason.</SheetDescription>
        </SheetHeader>

        {step === "form" && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Certificate #</Label>
                <Input value={certNumber} onChange={(e) => setCertNumber(e.target.value)} placeholder="Optional" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Issued Date</Label>
                <Input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Expiry Date</Label>
              <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Replace Document</Label>
              <p className="text-[11px] text-muted-foreground -mt-1">
                Printed on both sides? You can add the back of the card below.
              </p>
              {fileName ? (
                <div className="flex items-center gap-2 text-sm border rounded-md p-2">
                  <FileText className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="truncate flex-1">{fileName}</span>
                  <button type="button" onClick={() => { setFileId(null); setFileName(null); }}>
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              ) : isUploading ? (
                <div className="flex items-center gap-2 text-sm p-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{isCompressing ? "Optimizing…" : `Uploading… ${progress}%`}</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-1" /> Upload File
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => (canCaptureCamera() ? setCameraTarget(1) : cameraInputRef.current?.click())}>
                    <Camera className="h-4 w-4 mr-1" /> Take Photo
                  </Button>
                </div>
              )}
              <input ref={fileInputRef} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" onChange={handleFileChange} />
              <input ref={cameraInputRef} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} />
              <DocumentCapture
                open={cameraTarget !== null}
                onClose={() => setCameraTarget(null)}
                onCapture={(f) => void (cameraTarget === 2 ? uploadSecondPage(f) : uploadFirstPage(f))}
                onChooseFile={() => (cameraTarget === 2 ? cameraInputRef2 : cameraInputRef).current?.click()}
                title={cameraTarget === 2 ? "Take a photo of the back" : "Take a photo of the certificate"}
              />
            </div>

            {/* Optional back page — for licenses/cards printed on both sides */}
            {!showBackPage && (
              <button
                type="button"
                onClick={() => setShowBackPage(true)}
                className="text-xs text-blue-600 hover:underline"
              >
                + Add back of document (optional)
              </button>
            )}
            {showBackPage && (
              <div className="space-y-2">
                <Label className="text-xs">Back of Document <span className="text-muted-foreground ml-1">(optional)</span></Label>
                {fileName2 ? (
                  <div className="flex items-center gap-2 text-sm border rounded-md p-2">
                    <FileText className="h-4 w-4 text-green-600 shrink-0" />
                    <span className="truncate flex-1">{fileName2}</span>
                    <button type="button" onClick={() => { setFileId2(null); setFileName2(null); }}>
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                ) : isUploading2 ? (
                  <div className="flex items-center gap-2 text-sm p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{isCompressing2 ? "Optimizing…" : `Uploading… ${progress2}%`}</span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => fileInputRef2.current?.click()}>
                      <Upload className="h-4 w-4 mr-1" /> Upload File
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => (canCaptureCamera() ? setCameraTarget(2) : cameraInputRef2.current?.click())}>
                      <Camera className="h-4 w-4 mr-1" /> Take Photo
                    </Button>
                  </div>
                )}
                <input ref={fileInputRef2} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" onChange={handleFileChange2} />
                <input ref={cameraInputRef2} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange2} />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button className="flex-1" onClick={() => setStep("confirm")} disabled={isUploading}>
                Next: Confirm
              </Button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="mt-4 space-y-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
              To save changes, enter your password and explain why you're updating this record.
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reason for change <span className="text-red-500">*</span></Label>
              <Input
                placeholder="e.g. Renewed cert, wrong date entered…"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Your password <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleConfirmAndSave()}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep("form")} disabled={verifying || editMutation.isPending}>
                Back
              </Button>
              <Button className="flex-1" onClick={handleConfirmAndSave} disabled={verifying || editMutation.isPending}>
                {(verifying || editMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// --- Delete cert modal -----------------------------------------------------

function DeleteCertModal({
  cert,
  onSuccess,
  onClose,
}: {
  cert: UserCert;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [editReason, setEditReason] = useState("");
  const [verifying, setVerifying] = useState(false);

  const certName = cert.certTypeName ?? cert.customCertName ?? "this certification";

  const deleteMutation = useMutation({
    mutationFn: (data: { editReason?: string }) =>
      apiDelete(`/api/certifications/mine/${cert.id}`, data),
    onSuccess: () => {
      toast.success("Certification removed");
      onSuccess();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to remove");
    },
  });

  async function handleDelete() {
    if (!editReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setVerifying(true);
    try {
      const res = await apiPost<{ valid: boolean }>("/api/me/verify-password", { currentPassword: password });
      if (!res.valid) {
        toast.error("Incorrect password");
        setVerifying(false);
        return;
      }
    } catch {
      toast.error("Could not verify password");
      setVerifying(false);
      return;
    }
    setVerifying(false);

    deleteMutation.mutate({ editReason: editReason.trim() });
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="h-auto">
        <SheetHeader>
          <SheetTitle className="text-red-600">Remove Certification</SheetTitle>
          <SheetDescription>
            This will remove <strong>{certName}</strong> from your profile. The record is retained in the database for compliance.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Reason for removal <span className="text-red-500">*</span></Label>
            <Input
              placeholder="e.g. Entered by mistake, cert expired and replaced…"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Your password <span className="text-red-500">*</span></Label>
            <Input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDelete()}
            />
          </div>
          <div className="flex gap-2 pt-1 pb-4">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={verifying || deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
              disabled={verifying || deleteMutation.isPending}
            >
              {(verifying || deleteMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Remove
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// --- Add Certification Form -------------------------------------------------

function AddCertForm({
  onSuccess,
  onCancel,
  initialCertTypeId,
}: {
  onSuccess: () => void;
  onCancel: () => void;
  initialCertTypeId?: string;
}) {
  const { upload, isUploading, isCompressing, progress } = useFileUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Second page (e.g. back of a card/license) -- optional, own upload slot.
  const { upload: upload2, isUploading: isUploading2, isCompressing: isCompressing2, progress: progress2 } = useFileUpload();
  const fileInputRef2 = useRef<HTMLInputElement>(null);
  const cameraInputRef2 = useRef<HTMLInputElement>(null);
  const [cameraTarget, setCameraTarget] = useState<1 | 2 | null>(null);
  const [fileId2, setFileId2] = useState<string | null>(null);
  const [fileName2, setFileName2] = useState<string | null>(null);
  const [showBackPage, setShowBackPage] = useState(false);

  const [certTypeId, setCertTypeId] = useState("");
  const [selectedType, setSelectedType] = useState<CertType | null>(null);
  const [isOther, setIsOther] = useState(false);
  const [customCertName, setCustomCertName] = useState("");
  const [certNumber, setCertNumber] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const { data: typesData } = useOfflineQuery({
    queryKey: ["cert-types"],
    queryFn: () => apiGet<{ data: CertType[] }>("/api/certifications/types"),
    cacheKey: "offline:cert-types",
  });

  const certTypes = typesData?.data ?? [];

  // Pre-select the required type when opened from an "Assigned to You" warning.
  useEffect(() => {
    if (!initialCertTypeId || certTypeId || certTypes.length === 0) return;
    const t = certTypes.find((x) => x.id === initialCertTypeId);
    if (!t) return;
    setCertTypeId(t.id);
    setSelectedType(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCertTypeId, certTypes]);

  // Group by category for the dropdown
  const grouped = certTypes.reduce<Record<string, CertType[]>>((acc, t) => {
    const key = t.category ?? "safety";
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  const submitMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPost("/api/certifications/mine", data),
    onSuccess: () => {
      toast.success("Certification added");
      onSuccess();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to add certification");
    },
  });

  async function uploadFirstPage(file: File) {
    try {
      const result = await upload(file, { fileName: file.name, mimeType: file.type, purpose: "certification" });
      setFileId(result.fileId);
      setFileName(file.name);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFirstPage(file);
    e.target.value = "";
  }

  async function uploadSecondPage(file: File) {
    try {
      const result = await upload2(file, { fileName: file.name, mimeType: file.type, purpose: "certification" });
      setFileId2(result.fileId);
      setFileName2(file.name);
      toast.success("Back page uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function handleFileChange2(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadSecondPage(file);
    e.target.value = "";
  }

  function handleTypeChange(value: string) {
    if (value === "__other__") {
      setIsOther(true);
      setCertTypeId("");
      setSelectedType(null);
    } else {
      setIsOther(false);
      setCertTypeId(value);
      const t = certTypes.find((x) => x.id === value) ?? null;
      setSelectedType(t);
      if (t?.defaultValidityMonths && issuedDate) {
        const d = new Date(issuedDate);
        d.setMonth(d.getMonth() + t.defaultValidityMonths);
        setExpiryDate(d.toISOString().split("T")[0]);
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!isOther && !certTypeId) {
      toast.error("Please select a certification type");
      return;
    }
    if (isOther && !customCertName.trim()) {
      toast.error("Please enter a certification name");
      return;
    }
    if (!issuedDate) {
      toast.error("Issue date is required");
      return;
    }
    if (selectedType?.requiresFile !== false && !fileId) {
      toast.error("Please upload your certificate document");
      return;
    }

    const payload: Record<string, unknown> = { issuedDate };
    if (fileId) payload.fileId = fileId;
    if (fileId2) payload.fileId2 = fileId2;
    if (isOther) {
      payload.customCertName = customCertName.trim();
    } else {
      payload.certTypeId = certTypeId;
    }
    if (certNumber) payload.certNumber = certNumber;
    if (expiryDate) payload.expiryDate = expiryDate;
    if (notes) payload.notes = notes;

    submitMutation.mutate(payload);
  }

  const showExpiryField = isOther || !selectedType || selectedType.requiresExpiry;
  const fileRequired = isOther || !selectedType || selectedType.requiresFile;

  return (
    <Card>
      <CardContent className="py-4 px-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Certification Type</Label>
            {!isOther ? (
              <>
                <SearchableSelect
                  value={certTypeId}
                  onChange={handleTypeChange}
                  allLabel="Select a certification…"
                  allValue=""
                  keepOrder
                  searchPlaceholder="Type a certification name..."
                  options={[
                    ...(["trade", "safety", "training"] as const).flatMap((cat) =>
                      [...(grouped[cat] ?? [])]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((t) => ({ value: t.id, label: t.name, hint: CATEGORY_LABELS[cat] }))
                    ),
                    { value: "__other__", label: "Other (not listed)" },
                  ]}
                />
                {selectedType?.category && CATEGORY_COLORS[selectedType.category] && (
                  <div className={cn("flex items-center gap-2 px-3 py-2 rounded-md border text-sm", CATEGORY_COLORS[selectedType.category].bg)}>
                    <span className={cn("h-2 w-2 rounded-full shrink-0", CATEGORY_COLORS[selectedType.category].dot)} />
                    <span className={cn("font-medium", CATEGORY_COLORS[selectedType.category].text)}>
                      {CATEGORY_LABELS[selectedType.category]}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="Enter certification name"
                  value={customCertName}
                  onChange={(e) => setCustomCertName(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="text-xs text-blue-600 underline"
                  onClick={() => { setIsOther(false); setCustomCertName(""); }}
                >
                  Back to list
                </button>
                <p className="text-xs text-muted-foreground">
                  Custom entries are reviewed by an admin before being approved.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Certificate #</Label>
              <Input placeholder="Optional" value={certNumber} onChange={(e) => setCertNumber(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Issued Date <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={issuedDate}
                onChange={(e) => {
                  setIssuedDate(e.target.value);
                  if (selectedType?.defaultValidityMonths && e.target.value) {
                    const d = new Date(e.target.value);
                    d.setMonth(d.getMonth() + selectedType.defaultValidityMonths);
                    setExpiryDate(d.toISOString().split("T")[0]);
                  }
                }}
                required
              />
            </div>
          </div>

          {showExpiryField && (
            <div className="space-y-1">
              <Label className="text-xs">
                Expiry Date
                {selectedType?.defaultValidityMonths && (
                  <span className="ml-1 text-muted-foreground">
                    (auto-filled from issue date + {selectedType.defaultValidityMonths} months)
                  </span>
                )}
              </Label>
              <Input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input placeholder="Optional notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label className="text-xs">
              Certificate Document
              {fileRequired && <span className="text-red-500 ml-1">*</span>}
              {!fileRequired && <span className="text-muted-foreground ml-1">(optional)</span>}
            </Label>
            <p className="text-[11px] text-muted-foreground -mt-1">
              Printed on both sides? You can add the back of the card after uploading the front.
            </p>
            {fileName ? (
              <div className="flex items-center gap-2 text-sm border rounded-md p-2">
                <FileText className="h-4 w-4 text-green-600 shrink-0" />
                <span className="truncate flex-1">{fileName}</span>
                <button type="button" onClick={() => { setFileId(null); setFileName(null); }}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            ) : isUploading ? (
              <div className="flex items-center gap-2 text-sm p-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{isCompressing ? "Optimizing…" : `Uploading… ${progress}%`}</span>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-1" /> Upload File
                </Button>
                <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => (canCaptureCamera() ? setCameraTarget(1) : cameraInputRef.current?.click())}>
                  <Camera className="h-4 w-4 mr-1" /> Take Photo
                </Button>
              </div>
            )}
            <input ref={fileInputRef} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" onChange={handleFileChange} />
            <input ref={cameraInputRef} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} />
            <DocumentCapture
              open={cameraTarget !== null}
              onClose={() => setCameraTarget(null)}
              onCapture={(f) => void (cameraTarget === 2 ? uploadSecondPage(f) : uploadFirstPage(f))}
              onChooseFile={() => (cameraTarget === 2 ? cameraInputRef2 : cameraInputRef).current?.click()}
              title={cameraTarget === 2 ? "Take a photo of the back" : "Take a photo of the certificate"}
            />
          </div>

          {/* Optional back page — for licenses/cards printed on both sides */}
          {!showBackPage && !fileId2 && fileId && (
            <button
              type="button"
              onClick={() => setShowBackPage(true)}
              className="text-xs text-blue-600 hover:underline"
            >
              + Add back of document (optional)
            </button>
          )}
          {(showBackPage || fileId2) && (
            <div className="space-y-2">
              <Label className="text-xs">Back of Document <span className="text-muted-foreground ml-1">(optional)</span></Label>
              {fileName2 ? (
                <div className="flex items-center gap-2 text-sm border rounded-md p-2">
                  <FileText className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="truncate flex-1">{fileName2}</span>
                  <button type="button" onClick={() => { setFileId2(null); setFileName2(null); }}>
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              ) : isUploading2 ? (
                <div className="flex items-center gap-2 text-sm p-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{isCompressing2 ? "Optimizing…" : `Uploading… ${progress2}%`}</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => fileInputRef2.current?.click()}>
                    <Upload className="h-4 w-4 mr-1" /> Upload File
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => (canCaptureCamera() ? setCameraTarget(2) : cameraInputRef2.current?.click())}>
                    <Camera className="h-4 w-4 mr-1" /> Take Photo
                  </Button>
                </div>
              )}
              <input ref={fileInputRef2} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" onChange={handleFileChange2} />
              <input ref={cameraInputRef2} type="file" className="hidden" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange2} />
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={submitMutation.isPending || isUploading}>
              {submitMutation.isPending
                ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Saving…</>
                : "Save Certification"
              }
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
